import axios from "axios";
import { useContext, useEffect } from "react";
import { templateActionEnum } from "../Config/Constants";
import TemplateDataContext from "../Contexts/TemplateData.context";
import ErrorMessage from "../Util/ErrorMessage";
import {
  loadTemplate,
  modifiedTemplateJson,
  skipFirstStepJson,
} from "../Util/TemplateFunction";
import { fill } from "lodash";

type useLoadJsonType = {
  endPoint: string;
  jsonFileName: string;
  isModified?: boolean;
  state?: string;
  setIsContentLoaded?: (value: boolean) => void;
};

export default async function useLoadJson({
  endPoint,
  jsonFileName,
  isModified = true,
  state,
  setIsContentLoaded
}: useLoadJsonType): Promise<void> {
  const { state: templateState, dispatch } = useContext(TemplateDataContext);
  /* LOAD THE TEMPLATE  */
  useEffect(() => {
    dispatch({
      type: templateActionEnum.SET_NO_TEMPLATE_FOUND,
      payload: false,
    });
    const cancelToken = axios.CancelToken.source();
    const fetchTemplates = async () => {
      dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: true });
      try {
        const config = { cancelToken: cancelToken.token };
        dispatch({
          type: templateActionEnum.SET_TEMPLATE_PATH,
          payload: endPoint,
        });
        console.log("Loading template from:", endPoint, jsonFileName, config);
        const templateResult = await loadTemplate({
          endPoint,
          jsonFileName,
          config,
        });
        const { data } = templateResult;
        let filterTemplate: any = {};

        // If data already has temp keys (temp1, temp2, etc.), keep as is
        const dataKeys = Object.keys(data || {});
        const hasTempKeys = dataKeys.some((k) => /^temp\d+$/i.test(k));

        if (hasTempKeys) {
          filterTemplate = data;
        } else if (Array.isArray((data as any)?.steps)) {
          // JSON is a single template object containing steps; duplicate for temp1 and temp2
          filterTemplate = {
            temp1: data,
            temp2: data,
          };
        } else {
          // Fallback: copy enumerable props (legacy behavior)
          for (const key in data) {
            filterTemplate[key] = (data as any)[key];
          }
        }

        console.log(
          "[useLoadJson] normalized templates keys:",
          Object.keys(filterTemplate)
        );

        let templates = filterTemplate;
        if (isModified) templates = modifiedTemplateJson(filterTemplate);
        if (state) templates = skipFirstStepJson(templates);
        dispatch({
          type: templateActionEnum.LOAD_TEMPLATE,
          payload: templates,
        });
      } catch (error) {
        if (!axios.isCancel(error)) {
          dispatch({
            type: templateActionEnum.SET_NO_TEMPLATE_FOUND,
            payload: true,
          });
          dispatch({
            type: templateActionEnum.SET_ERROR,
            payload: { show: true, message: ErrorMessage.TEMPLATE_FAILED },
          });
        }
        dispatch({ type: templateActionEnum.SET_TEMPLATE, payload: {} });
      } finally {
        dispatch({ type: templateActionEnum.SET_IS_LOADING, payload: false });
        if (setIsContentLoaded) {
          setIsContentLoaded(true);
        }
      }
    };
    fetchTemplates();
    return () => cancelToken.cancel();
  }, [dispatch, endPoint, isModified, jsonFileName, state]);
}
